import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getBooks } from '../features/books/bookSlice';
import { getNews } from '../features/news/newsSlice';
import { getInformation } from '../features/information/informationSlice';
import { getSupportLogos } from '../features/supportLogos/supportLogosSlice';
import BookCard from '../components/BookCard';
import NewsCard from '../components/NewsCard';
import BookCardSkeleton from '../components/BookCardSkeleton';
import NewsCardSkeleton from '../components/NewsCardSkeleton';
import Modal from '../components/Modal';
import { Link } from 'react-router-dom';
import { getImageUrl } from '../utils/imageUrl';

const Home = () => {
  const dispatch = useDispatch();
  const { books, isLoading: booksLoading } = useSelector((state) => state.books);
  const { news, isLoading: newsLoading } = useSelector((state) => state.news);
  const { information, isLoading: infoLoading } = useSelector((state) => state.information);
  const { logos, isLoading: logosLoading } = useSelector((state) => state.supportLogos);
  const [selectedInfo, setSelectedInfo] = useState(null);

  useEffect(() => {
    dispatch(getBooks());
    dispatch(getNews());
    dispatch(getInformation());
    dispatch(getSupportLogos());
  }, [dispatch]);

  return (
    <div className="py-12">
      {/* Hero Section */}
      <section className="container mx-auto px-4 mb-16">
        <div className="bg-gradient-to-br from-dark-blue-700 to-dark-blue-900 rounded-3xl p-8 md:p-16 text-white">
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-5xl font-bold mb-6">
              Selamat Datang di Penerbit Literasiologi
            </h1>
            <p className="text-lg md:text-xl text-blue-100 mb-8">
              Penerbit Literasiologi hadir sebagai rumah bagi karya, literasi, dan kreativitas — membantu penulis mewujudkan naskah menjadi buku yang bermakna dan berdampak.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/books"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-dark-blue-700 rounded-xl font-medium hover:bg-blue-50 transition-colors"
              >
                Lihat Koleksi Buku
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <Link
                to="/news"
                className="inline-flex items-center gap-2 px-6 py-3 bg-dark-blue-600 text-white rounded-xl font-medium hover:bg-dark-blue-500 transition-colors border border-dark-blue-500"
              >
                Berita Terbaru
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Support Logos Section */}
      <section className="container mx-auto px-4 mb-16">
        {logosLoading ? (
          <div className="flex flex-wrap justify-center gap-8">
            {Array(4).fill(0).map((_, index) => (
              <div key={index} className="w-32 h-20 bg-gray-100 rounded-xl skeleton"></div>
            ))}
          </div>
        ) : logos.length > 0 ? (
          <div className="flex flex-wrap justify-center items-center gap-12">
            {logos.map((logo) => (
              <a
                key={logo._id}
                href={logo.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-40 h-24 bg-white rounded-2xl p-4 border border-gray-200 hover:shadow-lg gap-2 transition-all"
              >
                <img
                  src={getImageUrl(logo.logo)}
                  alt={logo.name}
                  className="max-w-full max-h-full object-contain"
                />
                <h3 className="text-center text-lg font-bold text-gray-700 mt-2">{logo.name}</h3>
              </a>
            ))}
          </div>
        ) : null}
      </section>

      {/* Books Section */}
      <section className="container mx-auto px-4 mb-16">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Buku Terbaru</h2>
            <p className="text-gray-500 mt-2">Koleksi buku terbitan Penerbit Literasiologi</p>
          </div>
          <Link to="/books" className="inline-flex items-center gap-2 text-dark-blue-700 font-medium hover:text-dark-blue-800 transition-colors">
            Lihat Semua
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {booksLoading ? (
            Array(8).fill(0).map((_, index) => <BookCardSkeleton key={index} />)
          ) : (
            books.slice(0, 8).map((book) => <BookCard key={book._id} book={book} />)
          )}
        </div>
      </section>

      {/* News Section */}
      <section className="container mx-auto px-4 mb-16">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Berita Terbaru</h2>
            <p className="text-gray-500 mt-2">Update kegiatan Penerbit Literasiologi</p>
          </div>
          <Link to="/news" className="inline-flex items-center gap-2 text-dark-blue-700 font-medium hover:text-dark-blue-800 transition-colors">
            Lihat Semua
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {newsLoading ? (
            Array(8).fill(0).map((_, index) => <NewsCardSkeleton key={index} />)
          ) : news.length > 0 ? (
            news.slice(0, 8).map((item) => (
              <NewsCard
                key={item._id}
                news={item}
              />
            ))
          ) : (
            <div className="col-span-full text-center py-20 bg-white rounded-2xl border border-gray-200">
              <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Belum ada berita</h3>
              <p className="text-gray-500">Berita akan segera ditampilkan</p>
            </div>
          )}
        </div>
      </section>

      {/* Information Section */}
      <section className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Informasi Penting</h2>
            <p className="text-gray-500 mt-2">Informasi terbaru tentang Penerbit Literasiologi</p>
          </div>
          <Link to="/information" className="inline-flex items-center gap-2 text-dark-blue-700 font-medium hover:text-dark-blue-800 transition-colors">
            Lihat Semua
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
        {infoLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Array(2).fill(0).map((_, index) => (
              <div key={index} className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="skeleton h-6 w-3/4 rounded-lg mb-4"></div>
                <div className="skeleton h-4 w-full rounded-lg mb-2"></div>
              </div>
            ))}
          </div>
        ) : information.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {information.slice(0, 2).map((info) => (
              <div key={info._id} className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-xl hover:border-dark-blue-200 cursor-pointer transition-all duration-300" onClick={() => setSelectedInfo(info)}>
                <div className="flex items-start justify-between mb-4">
                  <span className="px-3 py-1 bg-gradient-to-r from-dark-blue-500 to-dark-blue-700 text-white rounded-full text-sm font-medium">
                    {info.type}
                  </span>
                  {info.createdAt && (
                    <span className="text-xs text-gray-500">
                      {new Date(info.createdAt).toLocaleDateString('id-ID')}
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 line-clamp-2">{info.title}</h3>
                <p className="text-gray-600 text-sm">Klik untuk melihat detail</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-200">
            <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Belum ada informasi</h3>
            <p className="text-gray-500">Informasi akan segera ditampilkan</p>
          </div>
        )}
      </section>

      {/* Modal Detail Informasi */}
      {selectedInfo && (
        <Modal
          isOpen={!!selectedInfo}
          onClose={() => setSelectedInfo(null)}
          title={selectedInfo.title}
        >
          <div className="mb-4">
            <div className="flex items-center justify-between">
              <span className="px-3 py-1 bg-dark-blue-50 text-dark-blue-700 rounded-full text-sm font-medium">
                {selectedInfo.type}
              </span>
              {selectedInfo.createdAt && (
                <span className="text-xs text-gray-500">
                  {new Date(selectedInfo.createdAt).toLocaleDateString('id-ID')}
                </span>
              )}
            </div>
          </div>
          <div className="text-gray-600 leading-relaxed prose-content" dangerouslySetInnerHTML={{ __html: selectedInfo.content }} />
        </Modal>
      )}
    </div>
  );
};

export default Home;
